// PostHog consent wiring. Translates a user-level "granted" / "denied"
// choice into the SDK calls that switch between *cookieless* and
// *cookie-backed* capture.
//
// This is NOT an on/off switch — that is the whole point. The SDK is
// initialized with `cookieless_mode: 'on_reject'`, `opt_out_capturing_by_default`
// and consent-aware persistence (`instrumentation-client.ts`: 'memory' unless
// a stored analytics grant exists, in which case 'localStorage+cookie' so the
// previously persisted identity survives hard navigations), so the opted-out
// state still captures pageviews, autocapture and custom events while writing
// NOTHING to the device. ePrivacy Art. 5(3) governs device storage, not
// measurement, so that baseline needs no consent.
//
// `applyConsent('denied')` therefore means "keep measuring, cookielessly" —
// it is also the correct call for the no-decision case, and `ConsentBanner`
// uses it that way on mount. It doubles as the explicit `opt_out_capturing()`
// that posthog-js#2841 requires to get cookieless capture flowing.
//
// `applyConsent('granted')` moves the id onto disk, switches to cookie-backed
// identity, and starts session replay — which is the one thing that stays
// strictly consent-gated, because recording a session is categorically heavier
// processing than counting a pageview.
//
// Revoking switches persistence back to 'memory', which also clears whatever
// a previous grant wrote, and stops replay. Capture continues, cookielessly.
//
// Granting additionally hands the landing page's `utm_*` parameters to the
// store that opting in has just made writable (`landing-attribution.ts`). The
// SDK's own `$initial_utm_*` cannot be trusted at that point: it had nowhere to
// write them while opted out, so it registers them here — from whatever URL the
// visitor happens to be on when they click the banner.

import posthog from "posthog-js";

import type { ConsentValue } from "@/features/consent/consent-storage";
import { persistLandingUtm } from "@/integrations/posthog/landing-attribution";

interface PosthogWithReplay {
  __loaded?: boolean;
  opt_in_capturing: () => void;
  opt_out_capturing: () => void;
  set_config: (config: { persistence: "memory" | "localStorage+cookie" }) => void;
  startSessionRecording?: () => void;
  stopSessionRecording?: () => void;
}

export function applyConsent(value: ConsentValue): void {
  if (typeof window === "undefined") {
    return;
  }
  const ph = posthog as unknown as PosthogWithReplay;
  if (!ph.__loaded) {
    return;
  }
  if (value === "granted") {
    // Persist the id before opting in so captured events carry a stable id.
    ph.set_config({ persistence: "localStorage+cookie" });
    ph.opt_in_capturing();
    // AFTER `opt_in_capturing`, and that ordering is load-bearing rather than
    // stylistic. Under `cookieless_mode: 'on_reject'` posthog-js disables its
    // persistence outright while opted out, so a `register_once` issued any
    // earlier — including between `set_config` and here — is dropped in
    // silence, with no error to notice. `opt_in_capturing` is what re-enables
    // the store. Verified against posthog-js 1.415.2 in
    // `__tests__/landing-attribution.sdk.test.ts`; do not reorder these three.
    persistLandingUtm();
    ph.startSessionRecording?.();
  } else {
    // Replay is the only capability that goes fully dark without consent.
    ph.stopSessionRecording?.();
    // In `cookieless_mode: 'on_reject'` this does NOT silence the SDK — it
    // switches identity to the server-side hash and keeps events flowing.
    ph.opt_out_capturing();
    // Switching the persistence type clears the cookie/localStorage a prior
    // grant wrote and keeps any future id in memory only.
    ph.set_config({ persistence: "memory" });
  }
}
